<template>
  <v-row>
    <v-col cols="3">
      <planificacio-nav
        :isLoading="isLoading"
        :board="board"
        :globals="globals"
        :service="service"
        :headingOverride="headingOverride"
        :isPlanificacio="isPlanificacio"
        :resum="resum"
        :search-global="searchGlobal"
        :canEdit="canEdit"
        @updateSearchGlobalTerm="e => (searchGlobal = e)"
        @loadKanban="loadKanban"
        @filterItems="loadGlobals($event)"
        @OpenItemDialog="OpenItemDialog($event)"
        @openItemDialogRead="openItemDialogRead($event)"
      />
    </v-col>
    <v-col cols="9" class="columnContainer">
      <board-loading class="d-flex flex-nowrap fill-height" v-if="isLoading" />
      <draggable
        v-else
        v-bind="dragOptions"
        v-model="board"
        group="columns"
        tag="div"
        class="d-flex flex-nowrap fill-height"
        @end="onChangeColumn"
      >
        <v-card
          class="column grey lighten-3 mr-4"
          :class="{ expanded: !!expanded[column.id] }"
          v-for="column in board"
          :key="column.id"
          outlined
        >
          <v-toolbar
            dense
            absolute
            flat
            min-width="100%"
            :dark="column.pending"
            :class="column.pending ? 'primary' : ''"
            @dblclick="expand(column.id)"
          >
            <v-text-field
              v-if="isEdittingTitle === column.id && !column.pending"
              :label="column.title"
              v-model="newTitleName"
              solo
              flat
              append-icon="done"
              append-outer-icon="close"
              @click:append="saveTitle(column.id)"
              @click:append-outer="cancelSaveTitle"
              class="pl-0 editTitleField"
              :id="'editColumnTitle' + column.id"
            />

            <v-toolbar-title
              v-if="!column.pending && (isEdittingTitle === null || isEdittingTitle !== column.id)"
              class="columnTitle body-1 text-uppercase font-weight-medium"
            >
              <router-link v-if="column.click_action" :to="{ ...column.click_action }">
                {{ column.title }}
              </router-link>
              <span v-else>
                {{ column.title }}
              </span>
            </v-toolbar-title>
            <v-toolbar-title v-else class="body-1 text-uppercase font-weight-bold">
              {{ column.title }}
            </v-toolbar-title>

            <v-spacer
              v-if="!column.pending && (isEdittingTitle === null || isEdittingTitle !== column.id)"
            />
            <v-chip
              v-if="isEdittingTitle === null || isEdittingTitle !== column.id"
              class="ma-2"
              small
            >
              {{ column.children.length }}
            </v-chip>

            <v-menu
              v-if="!column.pending && (isEdittingTitle === null || isEdittingTitle !== column.id)"
              bottom
              left
              offset-y
              close-on-content-click
            >
              <template v-slot:activator="{ on }">
                <v-btn icon small v-on="on">
                  <v-icon size="18">more_vert</v-icon>
                </v-btn>
              </template>

              <v-list dense class="py-0">
                <template v-for="action in column.actions">
                  <v-list-item
                    v-if="isActionTypeFunction(action)"
                    :key="action.name"
                    @click.stop="customFunction(action.name, action.params)"
                  >
                    <v-list-item-content>
                      <v-list-item-title>{{ action.title }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item
                    v-else
                    :key="action.name"
                    :to="{ name: action.name, params: action.params }"
                    :target="action.target"
                  >
                    <v-list-item-content>
                      <v-list-item-title>{{ action.title }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
                <v-list-item v-if="canEdit" @click.stop="editTitle(column.id)">
                  <v-list-item-title>Canviar nom</v-list-item-title>
                </v-list-item>
                <v-list-item v-if="canEdit" @click.stop="deleteColumn(column.id)">
                  <v-list-item-title>Eliminar</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-toolbar>

          <draggable
            v-bind="dragOptions"
            v-model="column.children"
            :group="{
              name: 'items',
              put() {
                return canEdit
              },
              pull: true,
            }"
            tag="div"
            class="item-list pa-2 pt-12"
            :data-column="column.id"
            @end="onChangeItem"
            @add="onAddItem"
          >
            <v-card
              class="item mt-2"
              v-for="item in column.children"
              :key="item.id"
              :data-item="item.id"
              outlined
            >
              <item-title :item="item"></item-title>

              <v-card-text class="py-1 px-3" v-if="showChildren">
                <draggable
                  v-model="item.children"
                  :group="{
                    name: 'items',
                    put() {
                      return canEdit
                    },
                    pull: true,
                  }"
                  :class="{ 'px-3 py-2': !showChildren }"
                  tag="div"
                  outlined
                  @end="onChangeItem"
                  @add="onAddItem"
                  :data-column="column.id"
                  :data-item="item.id"
                >
                  <v-card
                    v-for="(subItem, i) in item.children"
                    :key="subItem.id"
                    :class="i !== 0 ? 'my-2' : ''"
                    class="subItem pa-1"
                    :data-item="subItem.id"
                    outlined
                  >
                    <item-title :item="subItem" />

                    <div class="d-flex justify-space-between align-center">
                      <customFieldsIcons
                        v-if="subItem.customFields.length > 0"
                        :customFieldsData="subItem.customFields"
                        :media="subItem.meta.media"
                      />
                      <v-spacer></v-spacer>
                      <v-btn icon small @click="OpenItemDialog(subItem.id)">
                        <v-icon size="15"> settings </v-icon>
                      </v-btn>
                    </div>
                  </v-card>
                </draggable>
              </v-card-text>

              <v-card-actions class="pa-3 pb-1 actions">
                <customFieldsIcons
                  v-if="item.customFields.length > 0"
                  :customFieldsData="item.customFields"
                />
                <v-spacer />
                <v-btn v-if="!isPlanificacio" icon x-small @click="OpenItemDialog(item.id)">
                  <v-icon size="15"> settings </v-icon>
                </v-btn>
                <v-btn icon x-small>
                  <v-icon
                    v-if="canEdit"
                    size="15"
                    @click.stop="deleteItem"
                    :data-column="column.id"
                    :data-item="item.id"
                    color="red"
                  >
                    delete
                  </v-icon>
                </v-btn>
              </v-card-actions>
            </v-card>
          </draggable>
        </v-card>

        <div v-if="canEdit" class="addColumn">
          <v-btn class="grey lighten-3" icon tile @click="addcolumn">
            <v-icon>add</v-icon>
          </v-btn>
        </div>
      </draggable>
    </v-col>
    <item-dialog
      v-if="itemDetailsDialog.itemId"
      :visible="itemDetailsDialog.visible"
      :itemId="itemDetailsDialog.itemId"
      :readOnly="itemDetailsDialog.readOnly"
      :canEdit="canEdit"
      :service="service"
      @toggleModalDetails="toggleModalDetails"
      @loadKanban="loadKanban"
    />
  </v-row>
</template>

<script>
export default {
  components: {
    draggable: () => import("vuedraggable"),
    planificacioNav: () => import("@/components/judo/PlanificacioNav"),
    BoardLoading: () => import("@/components/kanban/BoardLoading"),
    ItemDialog: () => import("@/components/kanban/ItemDialog"),
    customFieldsIcons: () => import("./KanbanJudoItemIcons"),
    itemTitle: () => import("./KanbanJudoItemTitle"),
  },
  props: {
    service: {
      type: Object,
      required: true,
    },
    showChildren: {
      type: Boolean,
      default: true,
    },
    showMedia: {
      type: Boolean,
      default: true,
    },
    customFunctions: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      board: [],
      canEdit: false,
      globals: {},
      searchGlobal: null,
      isEdittingTitle: null,
      isDragging: null,
      newTitleName: null,
      showTooltip: {},
      expanded: {},
      onChangeItemData: {
        action: null,
        item_id: null,
        from: {
          column_id: null,
          item_id: null,
          index: null,
        },
        to: {
          column_id: null,
          item_id: null,
          index: null,
        },
        meta: {},
      },
      onChangeColumnData: {
        id: null,
        position: null,
      },
      resumLoading: true,
      boardLoading: true,
      headingOverride: "",
      itemDetailsDialog: {
        readOnly: false,
        visible: false,
        itemId: null,
      },
      resum: [],
    }
  },
  methods: {
    deleteItem(e) {
      let message = null

      const itemData = e.target.dataset
      const columnId = parseInt(itemData.column)
      const itemId = parseInt(itemData.item)
      const column = this.board.filter(column => column.id === columnId)[0]
      const item = column.children.filter(item => item.id === itemId)[0]

      const itemIndex = column.children.findIndex(item => item.id === itemId)

      item.children.length > 0
        ? (message =
            "<p>Si elimines aquest mètode s'eliminarà també les activitats associades. Vols continuar?</p>")
        : (message = "<p>Confirmes que vols eliminar el següent element?</p>")

      message += "<p class='text-h5'>" + item.title + "</p>"

      if (this.isPlanificacio) {
        const data_item_id = e.target.attributes["data-item"].value
        const itemChildData = this.flatResum.filter(item => item.id === parseInt(data_item_id))[0]
        const itemChilds = itemChildData.children
        let hasChilds = itemChilds.length > 0

        if (hasChilds) {
          // If item has childs, add childs to message
          let itemChildList = ""
          for (const child of itemChilds) {
            itemChildList += "<span class='text-caption'>" + child.title + "</span><br/>"
          }

          message += "Activitats associades:<br/>" + itemChildList
        }
      }

      this.$confirm(message).then(confirm => {
        if (confirm) {
          column.children.splice(itemIndex, 1)

          const data = { ...this.onChangeItemData }
          data.action = "remove"
          data.item_id = itemId
          data.from = {}
          data.to = {}
          this.service
            .moveKanbanItem(data)
            .then(() => {
              this.loadResum()
            })
            .catch(error => {
              this.$notifyErrorByStatusCode(error.response.status)
            })
        }
      })
    },
    deleteColumn(columnId) {
      const column = this.board.filter(col => col.id === columnId)[0]
      this.$confirm(
        "<p>Confirmes que vols eliminar la següent columna?</p><h2 style='text-align:center'>" +
          column.title +
          "</h2>"
      ).then(confirm => {
        if (confirm) {
          this.service
            .removeKanbanColumn(columnId)
            .then(() => {
              const colIndex = this.board.findIndex(column => column.id === columnId)
              this.board.splice(colIndex, 1)
            })
            .catch(error => {
              this.$notifyErrorByStatusCode(error.response.status)
            })
        }
      })
    },
    editTitle(columnId) {
      const column = this.board.filter(col => col.id === columnId)[0]
      this.newTitleName = column.title
      this.isEdittingTitle = columnId
      setTimeout(() => {
        const inputId = "editColumnTitle" + columnId
        document.getElementById(inputId).focus()
      }, 0)
    },
    expand(columnId) {
      this.$set(this.expanded, columnId, !this.expanded[columnId])
    },
    saveTitle(columnId) {
      setTimeout(() => {
        const input = document.getElementById("editColumnTitle" + columnId)
        if (input) {
          const column = this.board.filter(col => col.id === columnId)[0]
          const title = input.value
          this.service
            .updateColumnTitle(columnId, title)
            .then(() => {
              column.title = title
              this.newTitleName = title
              this.isEdittingTitle = null
            })
            .catch(error => {
              this.$notifyErrorByStatusCode(error.response.status)
            })
        }
      }, 0)
    },
    cancelSaveTitle() {
      this.newTitleName = null
      this.isEdittingTitle = null
    },
    onAddItem(e) {
      if (e.from.attributes["data-column"] === undefined) {
        if (this.isPlanificacio) {
          const meta_id = JSON.parse(e.from.attributes["data-meta"].value).id

          const new_column_id = parseInt(e.to.attributes["data-column"].value)
          const column_items = this.board.find(column => column.id === new_column_id).children
          const repeatedItems = column_items.filter(item => item.meta.id === meta_id).length > 1

          //block moving repeated items
          if (repeatedItems) {
            this.$notify({
              type: "error",
              title: "Objectius duplicats",
              text: "Aquest objectiu ja existeix en aquest mesocicle.",
            })
            this.loadKanban()
            return false
          }
        }

        return this.udapteItem(e)
      }
    },
    onChangeItem(e) {
      if (!this.canEdit) {
        // reset positions
        const new_column_id = parseInt(e.to.attributes["data-column"].value)
        const column_items = this.board.find(column => column.id === new_column_id).children
        this.resetPositions(e, column_items)
        this.$notifyErrorByStatusCode(403)
        return false
      }
      const data_item_id = e.item.attributes["data-item"].value

      if (this.isPlanificacio) {
        const meta_id = this.flatResum.find(item => item.id === parseInt(data_item_id)).meta_id
        const old_column_id = parseInt(e.from.attributes["data-column"].value)
        const new_column_id = parseInt(e.to.attributes["data-column"].value)
        const column_items = this.board.find(column => column.id === new_column_id).children
        const repeatedItems = column_items.filter(item => item.meta.id === meta_id).length > 1

        //block moving repeated items
        if (repeatedItems) {
          this.$notify({
            type: "error",
            title: "Objectius duplicats",
            text: "Aquest objectiu ja existeix en aquest mesocicle.",
          })
          this.loadKanban()
          return false
        }

        const itemData = this.flatResum.filter(item => item.id === parseInt(data_item_id))[0]
        const itemChilds = itemData.children
        const hasChilds = itemChilds.length > 0

        // If item has childs, ask before move
        if (hasChilds && old_column_id !== new_column_id) {
          const itemtitle = itemData.title
          let itemChildList = ""
          for (const child of itemChilds) {
            itemChildList += "<span class='text-caption'>" + child.title + "</span><br/>"
          }

          this.$confirm(
            "<p>Aquest objectiu té activitats associades. Si segueixes també es canviaran les activitats, quedant pendents d'associar-les a un mesocicle. Vols continuar?</p>" +
              "<h4>" +
              itemtitle +
              "</h4>" +
              itemChildList,
            {
              title: "Objectiu amb activitats",
              color: "info",
              icon: "info",
              width: 500,
            }
          ).then(confirm => {
            confirm ? this.udapteItem(e) : this.loadKanban()
          })
        } else {
          this.udapteItem(e)
        }
      } else {
        this.udapteItem(e)
      }
    },
    udapteItem(e) {
      const data = { ...this.onChangeItemData }

      if (e.type === "add") {
        data.action = "add"
        data.from = {}
        data.to.index = e.newIndex
        data.to.column_id = e.to.attributes["data-column"].value
        data.meta = JSON.parse(e.from.attributes["data-meta"].value)
      } else {
        const data_item_id = e.item.attributes["data-item"].value

        data.action = "move"
        data.item_id = data_item_id
        data.from.index = e.oldIndex
        data.from.column_id = e.from.attributes["data-column"].value
        data.to.index = e.newIndex
        data.to.column_id = e.to.attributes["data-column"].value
        data.meta = {}
      }

      // FROM - is subitem?
      if (!e.from.classList.contains("item-list") && e.from.attributes["data-item"]) {
        data.from.item_id = e.from.attributes["data-item"].value
      } else {
        data.from.item_id = null
      }

      // TO - is subitem?
      if (!e.to.classList.contains("item-list")) {
        data.to.item_id = e.to.attributes["data-item"].value
      } else {
        data.to.item_id = null
      }

      this.service.moveKanbanItem(data)
        .then(response => {
          if (response.status === 201) {
            //update data-item attribute to the new Kanban item
            const old_id = JSON.parse(e.from.attributes["data-meta"].value).id
            const old_id_data = "globals_" + old_id
            const new_id = response.data.id

            // Update item id at this.board json
            const columnIndex = this.board.findIndex(
              column => column.id === parseInt(data.to.column_id)
            )

            const itemIndex = this.board[columnIndex].children.findIndex(
              item => item.id === old_id_data
            )

            if (this.isPlanificacio) {
              //is Planificacio View?
              this.board[columnIndex].children[itemIndex].id = new_id
            } else {
              // so... is Mesocicle
              if (this.board[columnIndex].children[itemIndex]) {
                // moving as main item?
                this.board[columnIndex].children[itemIndex].id = new_id
              } else {
                // so... moving as subitem. Then, look for subitem and update id.
                this.board[columnIndex].children.forEach(activitat => {
                  if (activitat.children.length > 0) {
                    activitat.children.forEach(subActivitat => {
                      if (subActivitat.id === old_id_data) {
                        subActivitat.id = new_id
                      }
                    })
                  }
                })
              }
            }
            this.loadGlobals()
            this.loadResum()
          }
        })
        .catch(error => {
          console.log(e)
          this.$notifyErrorByStatusCode(error.response.status)
        })
    },
    onChangeColumn(e) {
      if (!this.canEdit) {
        this.resetPositions(e, this.board)
        this.$notifyErrorByStatusCode(403)
        return false
      }
      const column_data = e.item.childNodes[1].dataset
      if (e.oldIndex !== e.newIndex) {
        this.service
          .moveKanbanColumn({
            ...this.onChangeColumnData,
            id: column_data.column,
            position: e.newIndex + 1,
          })
          .catch(error => {
            this.resetPositions(e, this.board)
            this.$notifyErrorByStatusCode(error.response.status)
          })
      }
    },
    addcolumn() {
      const id = this.$route.params["id"]
      this.service.addKanbanColumn(id)
        .then(response => {
          if (response.status === 201) {
            this.board.push(response.data)
          }
        })
        .catch(error => {
          this.$notifyErrorByStatusCode(error.response.status)
        })
    },
    loadKanban() {
      const id = this.$route.params["id"]
      this.service
        .getKanbanColumns(id)
        .then(response => {
          this.$emit("setHeading", response.data.nom)
          this.headingOverride = response.data.nom
          this.board = response.data.board
          this.boardLoading = false
          this.canEdit = response.data.canEdit
        })
        .catch(error => {
          this.$notify({
            type: "error",
            title: this.$store.state.commonText.notification.errorTitle,
            text: this.$store.state.commonText.notification.errorText + "<br>" + error,
          })
        })
    },
    loadResum() {
      const id = this.$route.params["id"]
      this.service
        .getResum(id)
        .then(response => {
          this.resum = response.data
        })
        .catch(error => {
          this.$notify({
            type: "error",
            title: this.$store.state.commonText.notification.errorTitle,
            text: this.$store.state.commonText.notification.errorText + "<br>" + error,
          })
        })
    },
    loadGlobals(data) {
      if (this.searchGlobal || data) {
        const formData = data ? data.formData : null
        const callback = data ? data.callback : null
        let postData = formData
          ? { search: this.searchGlobal, ...formData }
          : { search: this.searchGlobal }
        this.service
          .filterGlobals(postData)
          .then(response => {
            this.globals.items = response.data
          })
          .finally(() => {
            if (typeof callback === "function") {
              callback()
            }
          })
      } else {
        this.service.getGlobals().then(response => {
          if (response.status === 200) {
            this.globals = response.data
            this.resumLoading = false
          }
        })
      }
    },
    customFunction(functionName, args) {
      this.customFunctions[functionName].apply(null, args)
    },
    isActionTypeFunction(action) {
      return action.type !== undefined && action.type === "function"
    },
    toggleModalDetails() {
      this.itemDetailsDialog.visible = !this.itemDetailsDialog.visible
    },
    OpenItemDialog(itemId) {
      this.itemDetailsDialog.readOnly = false
      this.itemDetailsDialog.itemId = itemId
      this.toggleModalDetails()
    },
    openItemDialogRead(metaId) {
      this.itemDetailsDialog.readOnly = true
      this.itemDetailsDialog.itemId = metaId
      this.toggleModalDetails()
    },
    resetPositions(event, list) {
      let moved = list.splice(event.newIndex, 1)
      list.splice(event.oldIndex, 0, moved[0])
    },
  },
  computed: {
    dragOptions() {
      return {
        animation: 200,
        ghostClass: "ghost",
      }
    },
    isLoading() {
      return this.resumLoading || this.boardLoading
    },
    isPlanificacio() {
      return this.service.name() === "planificacions"
    },
    flatResum() {
      return this.resum.flat()
    },
  },
  beforeMount() {
    this.loadKanban()
    this.loadResum()
    this.loadGlobals()
  },
  mounted() {
    const parent = this
    this.$root.$on("refreshBoard", function () {
      parent.loadKanban()
    })
  },
  beforeDestroy() {
    this.$root.$off("refreshBoard")
  },
}
</script>

<style lang="scss">
.columnContainer {
  overflow-x: scroll;
}
.expanded {
  width: 65% !important;
  max-width: 550px !important;
  min-width: 350px !important;
}
.column {
  width: 250px;
  min-width: 250px;
  position: relative;
  display: flex;
  flex-direction: column;

  .columnTitle {
    cursor: pointer;
  }

  .columnTitle a {
    text-decoration: inherit;
    color: inherit;
  }

  .item-list {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    overflow-y: auto;
    overflow-x: hidden;

    .item {
      cursor: default;

      .actions {
        margin-top: -15px;
      }
    }

    .ghost {
      opacity: 0.2;
    }
  }
}

.addColumn {
  min-width: 100px;
}

.editTitleField {
  position: absolute;
  top: 0;
  left: 5%;
  bottom: 0;
  right: 5%;
}

.popover {
  z-index: 10;
}
</style>
